import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "./services/firebase";

// Enough to cover a practice's staff many times over without paging.
const MAX_REMEMBERED_NAMES = 50;

function namesKey(slug) {
  return ["submitter-names", slug];
}

/**
 * Doc id for a name, so the same person typed twice doesn't accumulate near
 * duplicates ("Dr. Smith", "dr smith "). Returns null when nothing usable is
 * left after normalizing, which is the caller's signal not to remember it.
 */
function nameId(name) {
  const id = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 100);
  return id || null;
}

/**
 * The submitter names a practice has used before, most recent first, plus a way
 * to add to the list.
 *
 * Stored at practices/{slug}/submitterNames/{nameId} and shared by everyone at
 * the practice — a name one person types is suggested to the rest of the team,
 * which is the whole point of remembering them (see firestore.rules, where
 * members read and write this list but only admins may delete from it).
 *
 * @param {string} slug - Practice slug (the Firestore practices doc id).
 * @returns {{ names: string[], isLoading: boolean, rememberName: Function }}
 */
export function useSubmitterNames(slug) {
  const queryClient = useQueryClient();

  const namesQuery = useQuery({
    queryKey: namesKey(slug),
    queryFn: async () => {
      const snap = await getDocs(
        query(
          collection(db, "practices", slug, "submitterNames"),
          orderBy("lastUsedAt", "desc"),
          limit(MAX_REMEMBERED_NAMES)
        )
      );
      return snap.docs.map((d) => d.data().name).filter(Boolean);
    },
    enabled: !!slug,
    staleTime: 1000 * 60 * 5,
  });

  const mutation = useMutation({
    mutationFn: async (name) => {
      const id = nameId(name || "");
      if (!id) return;
      await setDoc(
        doc(db, "practices", slug, "submitterNames", id),
        { name: name.trim(), lastUsedAt: serverTimestamp() },
        { merge: true }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: namesKey(slug) });
    },
  });

  return {
    names: namesQuery.data || [],
    isLoading: namesQuery.isLoading,
    rememberName: mutation.mutateAsync,
  };
}
